/**
 * The connection modal that sent a `whatsapp_connection.*` event, as its
 * `connection_flow` prop: the wizard or the current modal, so the funnels of
 * the two flows can be told apart.
 */
export type tWapiConnectionFlow = 'wizard' | 'legacy';

/**
 * The `connection_flow` of every connection event the current modal sends,
 * and of the dashboard card's reconnect, which launches Meta outside the
 * wizard.
 */
export const LEGACY_CONNECTION_FLOW: tWapiConnectionFlow = 'legacy';

export const WAPI_EVENTS = {
  // Each open of a connection modal, the wizard or the current one, so the
  // funnel starts before Meta does.
  INTEGRATION_CONNECTION_OPENED:
    'front.wapi.dashboard.integrations.whatsapp_connection.opened',
  INTEGRATION_CONNECTION_STARTED:
    'front.wapi.dashboard.integrations.whatsapp_connection.started',
  // INT-1516 — fires only on a connect response whose verdict is operational
  // (`useWhatsAppConnectState`). A held or failed 200 is not a connection, and
  // a hold that later heals is never counted here: every open tab observes that
  // flip, and WBS measures it (`wapi.activation.changed`).
  INTEGRATION_CONNECTION_SUCCESS:
    'front.wapi.dashboard.integrations.whatsapp_connection.success',
  INTEGRATION_CONNECTION_ERROR:
    'front.wapi.dashboard.integrations.whatsapp_connection.error',
  INTEGRATION_CONNECTION_RETRY_CLICKED:
    'front.wapi.dashboard.integrations.whatsapp_connection.retry_clicked',
  INTEGRATION_CONNECTION_CLOSED_BY_USER:
    'front.wapi.dashboard.integrations.whatsapp_connection.closed_by_user',
  INTEGRATION_CONNECTION_ESCALATED_VIEWED:
    'front.wapi.dashboard.integrations.whatsapp_connection.escalated_viewed',
  INTEGRATION_CONNECTION_ADVISOR_LIVE_CTA_CLICKED:
    'front.wapi.dashboard.integrations.whatsapp_connection.advisor_live_cta_clicked',
  INTEGRATION_CONNECTION_ADVISOR_SCHEDULE_CTA_CLICKED:
    'front.wapi.dashboard.integrations.whatsapp_connection.advisor_schedule_cta_clicked',
  INTEGRATION_CONNECTION_VIDEO_CLICKED:
    'front.wapi.dashboard.integrations.whatsapp_connection.connection_video_clicked',
  // The person left the wizard for the current modal in the same attempt.
  INTEGRATION_CONNECTION_CONNECT_WITHOUT_GUIDE_CLICKED:
    'front.wapi.dashboard.integrations.whatsapp_connection.connect_without_guide_clicked',
  INTEGRATION_DISCONNECTION:
    'front.wapi.dashboard.integrations.whatsapp_disconnection',

  // INT-1216 — Embedded Signup abandonment / error reporting. Meta posts both
  // to the browser during the popup; we used to discard them, leaving 93% of
  // connection failures with no diagnostic detail.
  //
  // ABANDONED carries current_step and arrives for 100% of abandonments — it is
  // what feeds the step-level funnel. ERROR_REPORTED carries Meta's error_code
  // but only when the customer presses «Reportar error a Leadsales», so its
  // coverage is partial by design on Meta's side.
  //
  // These are deliberately WAPI events even when the abandonment happens during
  // onboarding: it is one funnel regardless of where the popup was opened, and
  // the `surface` property on the payload distinguishes the entry point.
  INTEGRATION_CONNECTION_SIGNUP_ABANDONED:
    'front.wapi.dashboard.integrations.whatsapp_connection.signup_abandoned',
  INTEGRATION_CONNECTION_SIGNUP_ERROR_REPORTED:
    'front.wapi.dashboard.integrations.whatsapp_connection.signup_error_reported',

  // INT-1633 — the WhatsApp API module's exposure and depth, measured for the
  // workspaces that paid for it and have not connected yet (INT-1632). VIEW is
  // the module opening (once per mount, carries `entry_point`); each TAB_VIEW
  // is a section opened while the module is mounted. All carry
  // `connection_state`, because `wapi_integration` is a yes/no that cannot tell
  // "not connected yet" from "connected, held by Meta".
  GENERAL_PAGE_VIEW: 'front.wapi.general.page.view',
  TEMPLATES_TAB_VIEW: 'front.wapi.templates.tab.view',
  FLOWS_TAB_VIEW: 'front.wapi.flows.tab.view',
  CAMPAIGNS_TAB_VIEW: 'front.wapi.campaigns.tab.view',

  TEMPLATE_CREATE: 'front.wapi.templates.template.create.action',
  TEMPLATE_EDIT: 'front.wapi.templates.template.edit.action',
  TEMPLATE_DELETE: 'front.wapi.templates.template.delete.action',
  TEMPLATE_DRAFT_SAVE: 'front.wapi.templates.draft.save.action',

  // INT-1396 (U12) — the "Crear plantillas con IA" button.
  //
  // BUTTON_CLICK counts attempts; CONFIRMED counts generations the backend
  // actually accepted and queued. The two must NEVER be summed: the gap between
  // them is the funnel this feature is measured by. Same relationship as
  // CHAT_INTERACTIVE_SEND_ATTEMPTED vs _SUCCEEDED.
  //
  // COMPLETED and FAILED are emitted only from the branch where this session's
  // polling observed the batch settle — never from a mount-time read, or every
  // reload of the tab would re-report the same outcome.
  AI_TEMPLATES_BUTTON_CLICK: 'front.wapi.templates.ai_generation.button.click',
  AI_TEMPLATES_CONFIRMED: 'front.wapi.templates.ai_generation.confirmed.action',
  AI_TEMPLATES_CANCELLED: 'front.wapi.templates.ai_generation.cancelled.action',
  AI_TEMPLATES_EDUCATION_VIEWED:
    'front.wapi.templates.ai_generation.education.viewed',
  AI_TEMPLATES_COMPLETED: 'front.wapi.templates.ai_generation.completed.action',
  AI_TEMPLATES_FAILED: 'front.wapi.templates.ai_generation.failed.error',
  // Fires when the tab found a run already in flight and re-attached to it, which
  // is the only evidence that someone left and came back mid-generation.
  AI_TEMPLATES_REATTACHED:
    'front.wapi.templates.ai_generation.reattached.event',

  TEMPLATE_SYNC_START: 'front.wapi.templates.sync.start.action',
  TEMPLATE_SYNC_COMPLETE: 'front.wapi.templates.sync.complete.action',
  TEMPLATE_SYNC_ERROR: 'front.wapi.templates.sync.error',

  CAMPAIGN_BULK_SEND_START: 'front.wapi.campaigns.bulk_send.start.action',
  CAMPAIGN_BULK_SEND_COMPLETE: 'front.wapi.campaigns.bulk_send.complete.action',
  CAMPAIGN_BULK_SEND_ERROR: 'front.wapi.campaigns.bulk_send.error',
  CAMPAIGN_BULK_SEND_TEMPLATE_REJECTED:
    'front.wapi.campaigns.bulk_send.template_rejected.error',
  CAMPAIGN_BULK_SEND_RATE_LIMIT:
    'front.wapi.campaigns.bulk_send.rate_limit.error',
  CAMPAIGN_BULK_SEND_INVALID_NUMBER:
    'front.wapi.campaigns.bulk_send.invalid_number.error',

  TEMPLATE_STATUS_APPROVED: 'front.wapi.templates.status.approved.view',
  TEMPLATE_STATUS_REJECTED: 'front.wapi.templates.status.rejected.view',
  TEMPLATE_STATUS_RECATEGORIZED:
    'front.wapi.templates.status.recategorized.view',

  MESSAGE_TEMPLATE_MARKETING_SENT:
    'front.wapi.messages.template.marketing.sent.action',
  MESSAGE_TEMPLATE_MARKETING_FAILED:
    'front.wapi.messages.template.marketing.failed.error',
  MESSAGE_TEMPLATE_MARKETING_FREE_SENT:
    'front.wapi.messages.template.marketing.free_window.sent.action',
  MESSAGE_TEMPLATE_UTILITY_FREE_SENT:
    'front.wapi.messages.template.utility.free_window.sent.action',
  MESSAGE_TEMPLATE_UTILITY_SENT:
    'front.wapi.messages.template.utility.sent.action',
  MESSAGE_TEMPLATE_UTILITY_FAILED:
    'front.wapi.messages.template.utility.failed.error',

  MESSAGE_TEXT_SENT: 'front.wapi.messages.text.sent.action',
  MESSAGE_TEXT_FAILED: 'front.wapi.messages.text.failed.error',

  CHAT_ATTACHMENT_FILE_SENT: 'front.wapi.chat.attachment.file.sent.action',
  CHAT_ATTACHMENT_LOCATION_SENT:
    'front.wapi.chat.attachment.location.sent.action',
  CHAT_CALENDAR_EVENT_CREATE: 'front.wapi.chat.calendar.event.create.click',
  CHAT_ATTACHMENT_MEDIA_SENT: 'front.wapi.chat.attachment.media.sent.action',
  CHAT_AUDIO_SENT: 'front.wapi.chat.attachment.audio.sent.action',

  NOTIFICATION_VIEW: 'front.wapi.notification.view',

  CHAT_INTERACTIVE_OPEN: 'front.wapi.chat.interactive.open.click',
  CHAT_INTERACTIVE_TYPE_SELECTED:
    'front.wapi.chat.interactive.type.selected.click',
  CHAT_INTERACTIVE_SEND_ATTEMPTED:
    'front.wapi.chat.interactive.send.attempted.action',
  CHAT_INTERACTIVE_SEND_SUCCEEDED:
    'front.wapi.chat.interactive.send.succeeded.action',
  CHAT_INTERACTIVE_SEND_REJECTED:
    'front.wapi.chat.interactive.send.rejected.error',
  CHAT_INTERACTIVE_REPLY_RECEIVED:
    'front.wapi.chat.interactive.reply.received.event',
  CHAT_INTERACTIVE_QUICK_REPLY_SAVE_SUCCESS:
    'front.wapi.chat.interactive.quick_reply.save.success.action',
  CHAT_INTERACTIVE_QUICK_REPLY_SAVE_REJECTED:
    'front.wapi.chat.interactive.quick_reply.save.rejected.error',

  // INT-1197 — Flows (historial + builder onboarding tours, gestión,
  // publicación, sincronización con Meta, métricas y respuestas).
  FLOW_TOUR_LIST_VIEW_TUTORIAL_CLICK:
    'front.wapi.flows.tour.list.view_tutorial.click',
  FLOW_TOUR_LIST_COMPLETE: 'front.wapi.flows.tour.list.complete.action',
  FLOW_TOUR_LIST_SKIP: 'front.wapi.flows.tour.list.skip.action',
  FLOW_TOUR_BUILDER_VIEW_TUTORIAL_CLICK:
    'front.wapi.flows.tour.builder.view_tutorial.click',
  FLOW_TOUR_BUILDER_COMPLETE: 'front.wapi.flows.tour.builder.complete.action',
  FLOW_TOUR_BUILDER_SKIP: 'front.wapi.flows.tour.builder.skip.action',

  FLOW_CREATE: 'front.wapi.flows.flow.create.action',
  FLOW_CREATE_FROM_TEMPLATE: 'front.wapi.flows.flow.create_from_template.action',
  FLOW_EDIT: 'front.wapi.flows.flow.edit.click',
  FLOW_DRAFT_SAVE: 'front.wapi.flows.flow.draft_save.action',

  FLOW_PUBLISH: 'front.wapi.flows.flow.publish.action',
  FLOW_PUBLISH_ERROR: 'front.wapi.flows.flow.publish.error',

  FLOW_OBSOLETE: 'front.wapi.flows.flow.obsolete.action',

  FLOW_SYNC_START: 'front.wapi.flows.sync.start.action',
  FLOW_SYNC_COMPLETE: 'front.wapi.flows.sync.complete.action',
  FLOW_SYNC_ERROR: 'front.wapi.flows.sync.error',

  FLOW_METRICS_VIEW: 'front.wapi.flows.metrics.view.click',
  FLOW_RESPONSES_VIEW: 'front.wapi.flows.responses.view.click',

  // INT-1271 — fires when a workspace has no approved template of its own
  // (zero created, or some created but still pending Meta's review) and would
  // have fallen through to the generic system opening-template wrapper (blind
  // spot that got Be Clip SPAM-flagged twice in a week). The `reason` payload
  // property ('zero_templates' | 'pending_approval') distinguishes the two
  // sub-states; both redirect to the Plantillas tab.
  CHAT_NO_APPROVED_TEMPLATE_REDIRECT:
    'front.wapi.chat.no_approved_template.redirect.click',

  // RET-2339 — a plan without WhatsApp API (Básico, or Legacy without the WBS
  // access exception) meets a WhatsApp API surface it cannot use. VIEWED
  // counts exposures, the CLICK events count each way out; a purchase is
  // `front.settings.purchase_succeed` with the matching `cta_type`. Viewed
  // minus purchases is who saw it and did not buy.
  CHAT_PLAN_READ_ONLY_VIEWED: 'front.wapi.chat.plan_read_only.viewed',
  CHAT_PLAN_READ_ONLY_REPLY_BY_WEB_CLICK:
    'front.wapi.chat.plan_read_only.reply_by_web.click',
  CHAT_PLAN_READ_ONLY_UPGRADE_CLICK:
    'front.wapi.chat.plan_read_only.upgrade.click',
  CAMPAIGNS_PLAN_LOCK_VIEWED: 'front.wapi.campaigns.plan_lock.viewed',
  CAMPAIGNS_PLAN_LOCK_UPGRADE_CLICK:
    'front.wapi.campaigns.plan_lock.upgrade.click',
} as const;
